import { z } from "zod";
import { clinicalPhaseSchema } from "./clinical-phase";
import { medicalEvidenceTaskKeySchema } from "./medical-evidence";
import {
  COMPLETION_STANDARD_PATIENT_TEXT,
  completionSelectionSchema,
  completionSelectionToTransferDecision,
} from "./completion-picker";
import { formUploadMetaSchema } from "./intake-template";
import { prescriptionEvidenceReviewSchema } from "./prescription-evidence-review";
import { measurementReviewStatusSchema } from "./measurement-review";
import { preConsultSummarySchema } from "./preconsult-summary";
import { transferPlanChoiceViewSchema } from "./transfer-plan-choice";
import { prescriptionOrderSchema } from "./prescription-cycle";
import {
  transferPlannedMedicationSchema,
  transferReceptPlanChoiceSchema,
  transferReportedTreatmentSchema,
} from "./transfer-recept";
import {
  DOCTOR_NOTICE_UNCONFIRMED_CLAIM,
  doctorNoticeBodySchema,
  doctorNoticeUnconfirmedClaims,
} from "./doctor-notice";
import { confirmPrescriptionChoiceSchema } from "./treatment-start";
import {
  WEBDOC_LAB_ORDER_UNCONFIRMED,
  webdocControlSchema,
  webdocLabOrderConfirmed,
} from "./prescription-choice";

// TES-457 — the doctor's review of a prescription transfer (F-02 + F-07 in).
//
// The review row is the sole carrier of "where the review is" (Ö-2): the
// patient's journey state stays at `prescription_transfer_review_pending`
// through `meeting_required` and `completion_requested`, and only the two
// terminals move it (approved → doctor_visit_completed, cannot_transfer →
// ended). Source of truth for both the service's transition check and the
// admin's button set.

// TES-734 (Emelie 2026-09-16, A-02): the states in her words —
// SUBMITTED_FOR_ASYNC_REVIEW/UNDER_REVIEW = pending_review,
// COMPLETION_REQUIRED = completion_requested, LABS_REQUIRED = labs_required,
// APPROVED = approved, DECLINED = cannot_transfer. The internal names stay
// (the admin labels carry hers).
//
// TES-762 (Emelie 2026-09-17): `meeting_required` is ACTIVE again, as the
// fifth primary outcome — "Vårdmöte krävs före beslut". It was marked legacy
// for one day; this is a deliberate reversal, not a revert of TES-734.
//
// The meeting is NEED-DRIVEN, not a mandatory step for everyone: the doctor
// picks it when she cannot decide on paper. The case keeps its id and all its
// evidence, and after the meeting the SAME case returns to review — which is
// why `meeting_required` now has a way back to `pending_review` and did not
// before.
export const PRESCRIPTION_TRANSFER_REVIEW_STATUSES = [
  "pending_review",
  "meeting_required",
  "completion_requested",
  "labs_required",
  "approved",
  "cannot_transfer",
] as const;
export const prescriptionTransferReviewStatusSchema = z.enum(
  PRESCRIPTION_TRANSFER_REVIEW_STATUSES,
);
export type PrescriptionTransferReviewStatus = z.infer<
  typeof prescriptionTransferReviewStatusSchema
>;

export const PRESCRIPTION_TRANSFER_REVIEW_TRANSITIONS: Record<
  PrescriptionTransferReviewStatus,
  PrescriptionTransferReviewStatus[]
> = {
  pending_review: [
    "approved",
    "cannot_transfer",
    "completion_requested",
    "labs_required",
    "meeting_required",
  ],
  // TES-762 — after the meeting the case goes BACK to review with everything
  // it already had (returnTransferReviewToQueue, via the documented meeting).
  // The doctor can also decide straight from here when the meeting settled it.
  // Still no komplettering out of here: a doctor who has met her asks in the
  // room rather than sending her back to a form.
  meeting_required: ["pending_review", "approved", "cannot_transfer"],
  // TES-459 — the patient resubmits F-07 and the review reopens. The doctor
  // can also decide straight from here: an `other_medical` komplettering
  // arrives in the chat, not as a resubmit, so there is nothing to reopen.
  completion_requested: [
    "pending_review",
    "approved",
    "cannot_transfer",
    "labs_required",
    "meeting_required",
  ],
  // TES-734 — a new blood test was ordered. The result (TES-741) hands the
  // case back to review; the doctor can also decide straight from here.
  labs_required: [
    "pending_review",
    "approved",
    "cannot_transfer",
    "meeting_required",
  ],
  approved: [], // terminal
  cannot_transfer: [], // terminal
};

export function canTransitionTransferReview(
  from: PrescriptionTransferReviewStatus,
  to: PrescriptionTransferReviewStatus,
): boolean {
  return PRESCRIPTION_TRANSFER_REVIEW_TRANSITIONS[from].includes(to);
}

export const OPEN_TRANSFER_REVIEW_STATUSES: PrescriptionTransferReviewStatus[] =
  [
    "pending_review",
    "meeting_required",
    "completion_requested",
    "labs_required",
  ];

// What a doctor can ask the patient to redo (TES-459). The first four are
// F-07 section ids; `other_medical` is F-02 as a whole.
//
// TES-631 removed `blood_pressure_pulse` and `prior_labs` from F-07 (v2);
// TES-728 brought both back (v3); TES-761 removed them again (v4) and they are
// NO LONGER groups at all — blood pressure, pulse and earlier labs are asked
// for as CP-06 tasks the doctor picks explicitly (Emelie 2026-09-17).
//
// Rows stored before that may still name them. `completionSectionIds` resolves
// groups against the content it is handed, so such a row reopens nothing
// rather than crashing — which is also why the column stays a plain string[].
export const COMPLETION_GROUPS = [
  "current_treatment",
  "prescription_evidence",
  "other_medical",
] as const;
export const completionGroupSchema = z.enum(COMPLETION_GROUPS);
export type CompletionGroup = z.infer<typeof completionGroupSchema>;

// The F-07 reading the doctor sees next to the answers. Null when the
// submit found the values identical to the latest session and wrote none.
export const transferReviewReadingSchema = z.object({
  systolic: z.number().nullable(),
  diastolic: z.number().nullable(),
  pulse: z.number().nullable(),
  measuredAt: z.string(),
});
export type TransferReviewReading = z.infer<typeof transferReviewReadingSchema>;

// TES-911 (Ö-235) — the evidence the doctor already HAS, so he can decide
// whether to reuse it instead of asking for it again.
//
// Emelie 2026-09-24, asked what "tillräckligt aktuellt" should mean:
//
//   "Vi ska inte bygga fasta tidsgränser eller automatisk godkännande-/underkännandelogik i MVP."
//   "Systemet ska tydligt visa Kirill befintligt blodtryck, vilopuls och tidigare provsvar tillsammans med mät-/provtagningsdatum och källa."
//   "Systemet får gärna visa det senaste underlaget tydligt och kronologiskt, men ska inte fatta det kliniska beslutet åt läkaren."
//   "Formuleringen 'tillräckligt aktuellt' betyder därför i MVP: bedömt som tillräckligt aktuellt av ansvarig läkare."
//
// So this carries FACTS AND NO VERDICT. There is no `stale` flag, no age in
// days, no colour — deliberately, because any of them would be the threshold
// she declined to set, expressed as a hint. The date is here; the arithmetic
// is the doctor's.
export const existingReadingSchema = z.object({
  // When it was TAKEN, not when it was recorded — the distinction the
  // MeasurementSession model already draws, and the only one that matters
  // for judging currency.
  measuredAt: z.string(),
  // 'patient' | 'form' | 'clinician' | 'import'. Kept as the stored string
  // rather than narrowed to an enum here: a row written by an older
  // generation must still be displayable, and the admin labels what it
  // knows and shows the raw value otherwise.
  source: z.string(),
  // "health/trends", "F-02" — where it was entered. Null on rows that
  // predate the column.
  sourceSurface: z.string().nullable(),
  systolic: z.number().nullable(),
  diastolic: z.number().nullable(),
  pulse: z.number().nullable(),
  // Whether this is the reading submitted WITH the F-07 under review, so the
  // doctor can tell "arrived with this case" from "already on file". Without
  // it the newest row looks like history when it is the submission itself.
  fromThisReview: z.boolean(),
  // TES-1056 — the occasion, and the doctor's assessment of it
  // ("Patientrapporterat" until he sets one).
  sessionId: z.string().optional(),
  reviewStatus: measurementReviewStatusSchema.optional(),
});
export type ExistingReading = z.infer<typeof existingReadingSchema>;

// What she said about earlier lab results, from F-07's two optional fields
// (TES-843, v7). BOTH CAN BE NULL AND THAT IS INFORMATION, not a gap to
// hide: a result whose date she could not give is exactly the case where the
// doctor needs to see that he does not know when it was taken.
export const existingPriorLabsSchema = z.object({
  sampledAt: z.string().nullable(),
  source: z.string().nullable(),
  /** How many files she attached to the case. */
  attachments: z.number().int().nonnegative(),
});
export type ExistingPriorLabs = z.infer<typeof existingPriorLabsSchema>;

export const existingEvidenceSchema = z.object({
  /** Newest first — "det senaste underlaget tydligt och kronologiskt". */
  readings: z.array(existingReadingSchema),
  priorLabs: existingPriorLabsSchema.nullable(),
});
export type ExistingEvidence = z.infer<typeof existingEvidenceSchema>;

// An attachment as the case view lists it: the upload's metadata plus where
// it sat in the form, from the submitted version. `fieldLabel` is null only
// when the field no longer exists in that version's content (should not
// happen — versions are immutable once published — but the row must still
// render).
export const transferReviewUploadSchema = formUploadMetaSchema.extend({
  sectionId: z.string().nullable(),
  fieldLabel: z.string().nullable(),
  // TES-1057 / TES-1049 — the doctor's review of a prescription or order
  // document (an upload answering `pt_prescription_upload`). Null for any
  // other attachment (earlier lab results have their own review); absent in
  // a response from before TES-1057.
  evidenceReview: prescriptionEvidenceReviewSchema.nullable().optional(),
});
export type TransferReviewUpload = z.infer<typeof transferReviewUploadSchema>;

export const transferGapRiskLevelSchema = z.enum([
  "high",
  "medium",
  "low",
  "unknown",
]);
export type TransferGapRiskLevel = z.infer<typeof transferGapRiskLevelSchema>;

// TES-761/Ö-173 — `daysToNextDose` is gone with F-07 v4's dose dates. The
// risk is now read off remaining supply and prescription validity alone.
export const transferGapRiskSchema = z.object({
  level: transferGapRiskLevelSchema,
  remainingSupply: z.string().nullable(),
  daysToValidUntil: z.number().int().nullable(),
});
export type TransferGapRisk = z.infer<typeof transferGapRiskSchema>;

// TES-734 — the structured positive decision (A-02:
// "Godkännande kräver verifierad ordination, verified_treatment_phase=ACTIVE_TREATMENT eller MAINTENANCE, current_treatment_started_at och treatment_start_precision=EXACT eller ESTIMATED").
// The order is the same
// shape the onboarding approval uses (TES-636); the phase and the verified
// start date land on the Treatment, the order on the pending first cycle.
//
// TES-841 — the phase moved out of this file. It stopped being a transfer
// concept the moment Emelie required it after a care visit too, and the second
// value is `weight_stabilisation`, not `maintenance`. See ./clinical-phase.
export const TREATMENT_START_PRECISIONS = ["exact", "estimated"] as const;
export const treatmentStartPrecisionSchema = z.enum(TREATMENT_START_PRECISIONS);
export type TreatmentStartPrecision = z.infer<
  typeof treatmentStartPrecisionSchema
>;
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "date_required");
export const transferApprovalSchema = prescriptionOrderSchema.extend({
  clinicalPhase: clinicalPhaseSchema,
  currentTreatmentStartedAt: isoDate,
  treatmentStartPrecision: treatmentStartPrecisionSchema,
  // TES-1241 — the Recept section's planned medication: the two parts the
  // order does not hold, as the care meeting's (TES-1185), and whether the
  // doctor confirmed her reported treatment or changed it, with his reason.
  // Optional on the wire so an older admin bundle still approves; the
  // approval is saved as the planned medication either way.
  strength: z.string().trim().max(64).optional(),
  plan: z.string().trim().max(2000).optional(),
  plannedTreatment: transferReceptPlanChoiceSchema.optional(),
  planChangeReason: z.string().trim().max(2000).optional(),
});
export type TransferApproval = z.infer<typeof transferApprovalSchema>;

// TES-734 — the queue "Receptövertag att granska" (A-02:
// "patient, väntetid, status, ansvarig, riskflaggor, saknade underlag, nästa planerade dos, uppskattad mängd läkemedel kvar, receptets giltighet och risk för behandlingsglapp").
// Open rows only; sorted high gap risk first, then oldest.
//
// TES-804 — `assignee` is the patient's CURRENT responsible doctor, read from
// the handover log (latest row wins). It was hardcoded null until the log
// existed. Still null when nobody has been assigned, or when the assigned
// doctor is deactivated: a name nobody can reach answers the column worse
// than a dash does.
export const transferQueueRowSchema = z.object({
  reviewId: z.string().uuid(),
  patientId: z.string().uuid(),
  name: z.string(),
  personalNumberLast6: z.string(),
  status: prescriptionTransferReviewStatusSchema,
  // When the doctor's turn began: submit, or the latest resubmit.
  waitingSince: z.string(),
  // TES-892 (Ö-216) —
  // "Försenad status efter 24 bemannade vardagstimmar utan svar/aktiv hantering."
  // Derived server-side and measured in STAFFED time,
  // never wall-clock: 24 wall-clock hours from a Friday afternoon is a
  // Saturday, and calling a row late that nobody was ever there to handle
  // would train the doctor to ignore the badge.
  //
  // "Aktiv hantering" needs no separate test here, because `waitingSince`
  // already IS the moment the ball came back to the clinic — it moves with
  // every komplettering and every lab request. A row is late exactly when
  // nothing has moved it. Deliberately NOT gated on someone having merely
  // OPENED the case: a glance that changes nothing is what the badge exists
  // to surface, not a reason to hide it.
  //
  // Optional so an older API response still parses.
  delayed: z.boolean().optional(),
  assignee: z.object({ name: z.string() }).nullable(),
  openRiskFlags: z.number().int(),
  // What is still missing: the requested F-07 groups while a komplettering
  // is open, "new_blood_test" while labs are required.
  missing: z.array(z.string()),
  // TES-761 — `nextDoseAt` is gone: F-07 v4 no longer asks about the next
  // dose, and Emelie's decision explicitly removes it as an admin field for
  // continuity prioritisation. The queue is prioritised on remaining supply,
  // prescription validity and gapRisk instead.
  remainingSupply: z.string().nullable(),
  prescriptionValidUntil: z.string().nullable(),
  gapRisk: transferGapRiskSchema,
});
export type TransferQueueRow = z.infer<typeof transferQueueRowSchema>;

export const transferReviewSchema = z.object({
  id: z.string().uuid(),
  status: prescriptionTransferReviewStatusSchema,
  intakeSubmissionId: z.string().uuid(),
  transferSubmissionId: z.string().uuid(),
  requestedCompletionGroups: z.array(completionGroupSchema),
  completionRequestedAt: z.string().nullable(),
  decidedByDoctorId: z.string().nullable(),
  decidedAt: z.string().nullable(),
  reason: z.string().nullable(),
  // The 0 kr ConsultPayment a meeting_required decision minted (slice C;
  // legacy rows only since TES-734).
  waiverConsultPaymentId: z.string().nullable(),
  // TES-734 — when the doctor ordered a new blood test.
  labsRequestedAt: z.string().nullable(),
  // TES-734 — "fortsatt vårdväg" with a cannot_transfer: patient-facing.
  carePath: z.string().nullable(),
  // TES-734 — the structured approval as recorded (Treatment phase + the
  // order on the pending cycle); null until approved.
  approval: transferApprovalSchema.nullable(),
  // Her own answer to "När började din nuvarande behandlingsperiod?" —
  // shown read-only next to the doctor's verified date (A-02).
  selfReportedTreatmentStartedAt: z.string().nullable(),
  // TES-1241 — her current treatment from F-07, the Recept section's prefill,
  // with the ISO date her prescription is valid until (when she gave one)
  // and when F-07 was submitted. Optional so an older API still parses.
  reportedTreatment: transferReportedTreatmentSchema
    .extend({
      prescriptionValidUntilDate: z.string().nullable(),
      submittedAt: z.string().nullable(),
    })
    .nullable()
    .optional(),
  createdAt: z.string(),
  // The documents the patient attached to F-07 (metadata only — bytes via
  // employee-api/v1/patients/:id/uploads/:uploadId).
  uploads: z.array(transferReviewUploadSchema),
  reading: transferReviewReadingSchema.nullable(),
  // TES-911 (Ö-235) — everything already on file, beside the one reading
  // that came with this submission.
  existingEvidence: existingEvidenceSchema,
  // TES-458 — the case view. Both underlag rendered against the template
  // VERSION each was submitted under (never the currently published one, so
  // a wording change after submit cannot desync what the doctor reads from
  // what the patient answered), and F-02 is the submission the review was
  // OPENED on (`intakeSubmissionId`), not the patient's latest intake. Null
  // when that version is no longer resolvable.
  transferSummary: preConsultSummarySchema.nullable(),
  intakeSummary: preConsultSummarySchema.nullable(),
  templateVersions: z.object({
    transfer: z.number().int().nullable(),
    intake: z.number().int().nullable(),
  }),
  // Who decided, by display name — `decidedByDoctorId` is a soft id the
  // portal cannot resolve on its own.
  decidedBy: z.object({ name: z.string() }).nullable(),
  // TES-728 — risk of a treatment gap, derived from F-07 v3's next dose,
  // remaining supply and prescription validity. A queue-ordering signal for
  // the doctor, never patient-visible and never a medical conclusion.
  // Optional so an admin built before it still parses a newer server.
  gapRisk: transferGapRiskSchema.nullable().optional(),
  // TES-1034 S8 — her subscription choice from F-07, its eligibility and
  // the doctor's correction, if any. Optional so an older API parses.
  planChoice: transferPlanChoiceViewSchema.nullable().optional(),
});
export type TransferReview = z.infer<typeof transferReviewSchema>;

// Decision body (slice B, reshaped by TES-734 to the four outcomes).
//   approved             → `approval` (order + phase + verified start date)
//   completion_requested → `completionGroups` (≥1) + `reason` (patient-visible)
//   labs_required        → `reason` (patient-visible)
//   cannot_transfer      → `reason` (patient-visible medical motivation) +
//                          `carePath` (patient-visible "fortsatt vårdväg")
// TES-762 — five primary outcomes. `meeting_required` mints a 0 kr
// ConsultPayment waiver so she can book without paying (TES-457): the booking
// gate only knows paid ConsultPayments, and teaching it a bypass flag would
// be something a client could forge.
export const TRANSFER_REVIEW_OUTCOMES = [
  "approved",
  "cannot_transfer",
  "completion_requested",
  "labs_required",
  "meeting_required",
] as const;
export const transferReviewDecisionSchema = z
  .object({
    outcome: z.enum(TRANSFER_REVIEW_OUTCOMES),
    reason: z.string().trim().max(2000).optional(),
    carePath: z.string().trim().max(2000).optional(),
    completionGroups: z.array(completionGroupSchema).optional(),
    // TES-763 — targeted completion: exactly the CP-06 tasks the doctor chose.
    //
    // Distinct from `completionGroups`, which are F-07's form sections. This
    // list is EXHAUSTIVE when set: only what is in it is created, and above
    // all no lab payment is created that the patient was never asked to make
    // ("riktad komplettering skapar ingen implicit provbetalning").
    evidenceTasks: z.array(medicalEvidenceTaskKeySchema).optional(),
    approval: transferApprovalSchema.optional(),
    // TES-950 — the shared completion picker. On `completion_requested` the
    // admin sends the doctor's items here and the SERVER resolves them into
    // `completionGroups` + `evidenceTasks` (`resolveTransferDecision`), so the
    // two stored vocabularies are never picked by hand again. No reason is
    // required with it (Ö-243's assumption (a)): the patient reads
    // COMPLETION_STANDARD_PATIENT_TEXT instead.
    completion: completionSelectionSchema.optional(),
    // TES-1187 (TES-1180 point 9) — the transfer follows the treatment
    // decision (TES-1185): an approval carries its prescription status,
    // "Receptet är utfärdat i Webdoc" or "Befintligt recept gäller
    // fortfarande", registered in the same publish and released by the
    // verified subscription. Only on an approval; optional on the wire so an
    // older admin bundle keeps the two-step order (Steg B's ticks, then
    // "Bekräfta receptstatus").
    prescription: confirmPrescriptionChoiceSchema.optional(),
    // TES-1187 — "Läkarens besked till patienten · visas under Din vård"
    // (TES-1201) on an approval; the other outcomes speak through `reason`.
    patientNotice: doctorNoticeBodySchema.optional(),
    // TES-1242 — the Recept section's plan on a komplettering or a new blood
    // test: saved as the planned medication, so the case comes back with it
    // prefilled. Never a prescription.
    plannedMedication: transferPlannedMedicationSchema.optional(),
    // The remiss confirmation, required whenever the decision asks for a
    // new blood test ("Nytt blodprov krävs", or the picker's item).
    webdocControl: webdocControlSchema.optional(),
  })
  .superRefine((v, ctx) => {
    if (
      v.plannedMedication &&
      v.outcome !== "completion_requested" &&
      v.outcome !== "labs_required"
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["plannedMedication"],
        message: "planned_medication_only_with_completion",
      });
    }
    if (
      v.plannedMedication?.planChangeReason &&
      v.plannedMedication.plannedTreatment !== "changed"
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["plannedMedication", "planChangeReason"],
        message: "plan_change_reason_only_with_changed",
      });
    }
    const newBloodTest =
      v.outcome === "labs_required" ||
      (v.outcome === "completion_requested" &&
        (v.completion?.items.includes("new_blood_test") ||
          v.evidenceTasks?.includes("new_blood_test")));
    if (newBloodTest && !webdocLabOrderConfirmed(v.webdocControl)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["webdocControl", "labOrderDoneInWebdoc"],
        message: WEBDOC_LAB_ORDER_UNCONFIRMED,
      });
    }
    if (v.completion) {
      if (v.outcome !== "completion_requested") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["completion"],
          message: "completion_only_with_completion_requested",
        });
      }
      // The picker is the whole request; hand-picked lists beside it would
      // be a second, possibly different, answer to the same question.
      if (v.completionGroups?.length || v.evidenceTasks?.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["completion"],
          message: "completion_excludes_groups_and_tasks",
        });
      }
      return;
    }
    if (v.outcome !== "approved" && !v.reason) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["reason"],
        message: "reason_required",
      });
    }
    if (v.prescription && v.outcome !== "approved") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["prescription"],
        message: "prescription_only_with_approved",
      });
    }
    if (v.patientNotice && v.outcome !== "approved") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["patientNotice"],
        message: "notice_only_with_approved",
      });
    }
    // TES-1201 — the notice says nothing exists before its Webdoc
    // confirmation; whether she may be told about the prescription before
    // her subscription is active is the API's check (TES-1185).
    if (
      v.patientNotice &&
      doctorNoticeUnconfirmedClaims(v.patientNotice, {
        labOrder: false,
        prescription: v.prescription?.choice === "new_issued",
      }).length > 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["patientNotice"],
        message: DOCTOR_NOTICE_UNCONFIRMED_CLAIM,
      });
    }
    if (v.outcome === "approved" && !v.approval) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["approval"],
        message: "approval_required",
      });
    }
    // TES-1241 — a reason is the reason for a change.
    if (
      v.approval?.planChangeReason &&
      v.approval.plannedTreatment !== "changed"
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["approval", "planChangeReason"],
        message: "plan_change_reason_only_with_changed",
      });
    }
    if (v.outcome !== "approved" && v.approval) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["approval"],
        message: "approval_only_with_approved",
      });
    }
    if (v.outcome === "cannot_transfer" && !v.carePath) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["carePath"],
        message: "care_path_required",
      });
    }
    const groups = v.completionGroups ?? [];
    // TES-901 — Emelie 2026-09-23, slutregeln: "Publicering stoppas bara om
    // varken kompletteringsgrupp eller konkret uppgift har valts."
    //
    // This used to demand a group ALWAYS, which forbade a komplettering that
    // asks only for new evidence (a blood pressure reading, say) without
    // reopening any F-07 section. The two controls drive two different
    // patient paths — groups reopen her F-07 scoped to those sections,
    // tasks open a CP-06 checklist — and either one on its own is a real
    // komplettering. What is not real is neither: that decision reaches her
    // with nothing to do, and THAT is what the block is for.
    if (
      v.outcome === "completion_requested" &&
      groups.length === 0 &&
      (v.evidenceTasks ?? []).length === 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["completionGroups"],
        message: "completion_needs_group_or_task",
      });
    }
    if (v.outcome !== "completion_requested" && groups.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["completionGroups"],
        message: "completion_groups_only_with_completion_requested",
      });
    }
    // TES-763 — targeted tasks belong to a completion request or a lab
    // request, i.e. the two outcomes that OPEN a CP-06. Sending them with an
    // approval or a rejection would be asking for information in a case that
    // has just been closed.
    const tasks = v.evidenceTasks ?? [];
    if (
      tasks.length > 0 &&
      v.outcome !== "completion_requested" &&
      v.outcome !== "labs_required"
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["evidenceTasks"],
        message: "evidence_tasks_only_with_open_outcome",
      });
    }

    // TES-901 —
    // "Samma informationsbehov får inte efterfrågas både via återöppnad F-07 och CP-06. Förebygg eller varna för överlapp, särskilt prescription_evidence/receptunderlag."
    //
    // Prevent rather than warn: a warning in the admin panel does not protect
    // the API, and this is server-testable. `prescription_evidence` is the one
    // key that lives in BOTH vocabularies, so it is the only way to ask for
    // the same thing twice — once as a reopened F-07 section, once as a CP-06
    // upload. The doctor picks one.
    const overlapping = groups.filter((g) =>
      (tasks as readonly string[]).includes(g),
    );
    if (overlapping.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["evidenceTasks"],
        message: `asked_twice:${overlapping.join(",")}`,
      });
    }
  });
export type TransferReviewDecision = z.infer<
  typeof transferReviewDecisionSchema
>;

// TES-950 — a picker decision in the shape the rest of the review code
// already reads: groups, tasks and a patient-visible reason. A decision
// without `completion` is returned unchanged.
//
// `other` is chat only (Ö-246), so a komplettering that names nothing else
// resolves to no group and no task. That is still a komplettering the doctor
// chose, and the chat message is what she acts on. TES-909:
// "Publicering ska stoppas endast om läkaren inte har valt något kompletteringsbehov alls."
export function resolveTransferDecision(
  decision: TransferReviewDecision,
): TransferReviewDecision {
  if (!decision.completion) return decision;
  const mapped = completionSelectionToTransferDecision(
    decision.completion.items,
  );
  return {
    ...decision,
    completionGroups: mapped.completionGroups,
    evidenceTasks: mapped.evidenceTasks,
    reason: decision.reason || COMPLETION_STANDARD_PATIENT_TEXT,
  };
}

// TES-892 (Ö-216), Emelie 2026-09-23, verbatim:
// "Försenad status efter 24 bemannade vardagstimmar utan svar/aktiv hantering."
export const TRANSFER_REVIEW_DELAYED_STAFFED_HOURS = 24;
